import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getShifts as getOnlineShifts } from '../../utils/api';
import { getShifts as getOfflineShifts } from '../../utils/offlineDb';
import {
    startOfDay,
    endOfDay,
    subDays,
    isWithinInterval,
    roundToNearestMinutes,
} from 'date-fns';
import { Calendar, Clock, RefreshCw, ChevronDown } from 'lucide-react';
import { LoadingSkeleton } from '../common/LoadingSkeleton';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { calculateDuration } from '../../utils/time';
import { WorkerShift } from '../../types/auth';
import { formatInTimeZone } from 'date-fns-tz';

interface Shift {
    worker_id: number;
    clockin_time: number;
    finishjob_time?: number;
    clockout_time?: number;
    createdAt: number;
}

interface ShiftStats {
    totalShifts: number;
    totalHours: number;
    averageShiftLength: string;
}

function ShiftCard({ shift }: { shift: Shift }) {
    const clockInDate = new Date(shift.clockin_time * 1000);
    const finishJobDate = shift.finishjob_time
        ? new Date(shift.finishjob_time * 1000)
        : null;
    const clockOutDate = shift.clockout_time
        ? new Date(shift.clockout_time * 1000)
        : null;

    const duration = clockOutDate
        ? calculateDuration(
              shift.clockin_time * 1000,
              shift.clockout_time! * 1000,
          )
        : 'In Progress';

    return (
        <div className='card p-6'>
            {/* Header with Date and Duration */}
            <div className='flex justify-between items-center mb-6'>
                <div className='flex items-center gap-3'>
                    <Calendar className='h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0' />
                    <div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                            Date
                        </div>
                        <div className='font-medium text-gray-900 dark:text-gray-100'>
                            {formatInTimeZone(
                                clockInDate,
                                'America/Chicago',
                                'MMM d, yyyy',
                            )}
                        </div>
                    </div>
                </div>

                <div className='flex items-center gap-3'>
                    <Clock className='h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0' />
                    <div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                            Duration
                        </div>
                        <div className='font-medium text-gray-900 dark:text-gray-100'>
                            {duration}
                        </div>
                    </div>
                </div>
            </div>

            {/* Time Information */}
            <div className='flex justify-around items-center'>
                <div className='text-center px-2'>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                        Clock In
                    </div>
                    <div className='font-medium text-gray-900 dark:text-gray-100'>
                        {formatInTimeZone(
                            clockInDate,
                            'America/Chicago',
                            'h:mm a',
                        )}
                    </div>
                </div>

                {finishJobDate && (
                    <div className='text-center px-2'>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                            Job Finish
                        </div>
                        <div className='font-medium text-gray-900 dark:text-gray-100'>
                            {formatInTimeZone(
                                finishJobDate,
                                'America/Chicago',
                                'h:mm a',
                            )}
                        </div>
                    </div>
                )}

                {clockOutDate && (
                    <div className='text-center px-2'>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>
                            Clock Out
                        </div>
                        <div className='font-medium text-gray-900 dark:text-gray-100'>
                            {formatInTimeZone(
                                clockOutDate,
                                'America/Chicago',
                                'h:mm a',
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatsCard({ stats }: { stats: ShiftStats }) {
    return (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
            <div className='card p-4'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                    Total Shifts
                </div>
                <div className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
                    {stats.totalShifts}
                </div>
            </div>

            <div className='card p-4'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                    Total Hours
                </div>
                <div className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
                    {stats.totalHours.toFixed(1)}
                </div>
            </div>

            <div className='card p-4'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                    Average Shift
                </div>
                <div className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
                    {stats.averageShiftLength}
                </div>
            </div>
        </div>
    );
}

export function ShiftsView() {
    const navigate = useNavigate();
    const { token, workerId, isAuthenticated } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [dateFilter, setDateFilter] = useState<
        'all' | 'today' | 'week' | 'month'
    >('all');
    const [stats, setStats] = useState<ShiftStats>({
        totalShifts: 0,
        totalHours: 0,
        averageShiftLength: '0h 0m',
    });

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const calculateStats = (shifts: Shift[]) => {
        const completedShifts = shifts.filter((shift) => shift.clockout_time);
        const totalHours = completedShifts.reduce((acc, shift) => {
            const duration = shift.clockout_time! - shift.clockin_time;
            return acc + duration / 3600; // Convert seconds to hours
        }, 0);

        const averageSeconds =
            completedShifts.length > 0
                ? (totalHours * 3600) / completedShifts.length
                : 0;

        return {
            totalShifts: completedShifts.length,
            totalHours,
            averageShiftLength: calculateDuration(0, averageSeconds * 1000),
        };
    };

    const fetchShifts = async () => {
        if (!token || !workerId) {
            setError('Please log in to view shifts');
            navigate('/login');
            return;
        }

        setLoading(true);
        try {
            let shiftsData;
            if (!navigator.onLine) {
                shiftsData = await getOfflineShifts();
            } else {
                shiftsData = await getOnlineShifts(token);
            }

            const workerShifts: WorkerShift[] = shiftsData.filter(
                (shift) => shift.worker_id === parseInt(workerId, 10),
            );
            const shiftData: Shift[] = workerShifts.map((shift) => ({
                worker_id: shift.worker_id!,
                clockin_time: shift.clockin_time,
                finishjob_time: shift.finishjob_time,
                clockout_time: shift.clockout_time,
                createdAt: shift.createdAt,
            }));
            setShifts(shiftData);
            setStats(calculateStats(shiftData));
            setError(null);
        } catch (error: any) {
            setError(error.message || 'Failed to fetch shifts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token && workerId) {
            fetchShifts();
        }
    }, [token, workerId]);

    const filteredShifts = shifts
        .filter((shift) => {
            const shiftDate = new Date(shift.clockin_time * 1000);
            const now = new Date();

            switch (dateFilter) {
                case 'today':
                    return isWithinInterval(shiftDate, {
                        start: startOfDay(now),
                        end: endOfDay(now),
                    });
                case 'week':
                    return isWithinInterval(shiftDate, {
                        start: startOfDay(subDays(now, 7)),
                        end: endOfDay(now),
                    });
                case 'month':
                    return isWithinInterval(shiftDate, {
                        start: startOfDay(subDays(now, 30)),
                        end: endOfDay(now),
                    });
                default:
                    return true;
            }
        })
        .sort((a, b) => b.clockin_time - a.clockin_time);

    if (loading) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <ErrorState message={error} onRetry={fetchShifts} />
            </div>
        );
    }

    return (
        <div className='max-w-4xl mx-auto p-4'>
            {isOffline && (
                <div className='mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg flex items-center gap-2'>
                    <span className='text-sm'>
                        You are currently offline. Some features may be limited.
                    </span>
                </div>
            )}
            <div className='flex justify-end items-center mb-6'>
                <div className='flex items-center gap-2'>
                    <div className='relative'>
                        <select
                            value={dateFilter}
                            onChange={(e) =>
                                setDateFilter(e.target.value as any)
                            }
                            className='appearance-none bg-light-card dark:bg-dark-card text-gray-900 dark:text-gray-100 px-4 py-2 pr-10 rounded-lg border border-light-border dark:border-dark-border shadow-soft dark:shadow-soft-dark hover:border-primary-500 dark:hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all'
                            style={{ minWidth: '140px' }}
                        >
                            <option value='all'>All Time</option>
                            <option value='today'>Today</option>
                            <option value='week'>Last 7 Days</option>
                            <option value='month'>Last 30 Days</option>
                        </select>
                        <ChevronDown className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none' />
                    </div>
                    <button
                        onClick={fetchShifts}
                        disabled={loading}
                        className='p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors disabled:opacity-50'
                        title='Refresh shifts'
                    >
                        <RefreshCw
                            className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`}
                        />
                    </button>
                </div>
            </div>

            <StatsCard stats={stats} />

            {filteredShifts.length === 0 ? (
                <EmptyState message='No shifts found' />
            ) : (
                <div className='space-y-4'>
                    {filteredShifts.map((shift) => (
                        <ShiftCard
                            key={`${shift.worker_id}-${shift.clockin_time}`}
                            shift={shift}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
